import React from "react";
import { bind } from "decko";

import DialogOverlay from "./DialogOverlay";
import ButtonSelectionGroup from "./ButtonSelectionGroup";
import StopClockOverlay from "./StopClockOverlay";

/**
 * UI for updating current phase of a game, i.e. first half, second half etc.
 * Creates an event to signal the start of each phase, or end of game.
 *
 * FIXME: This uses Game.phase which is not relative to the playback time,
 * so this is a little hard to test, as you can only export each phase change once per game.
 * To circumvent in testing, we have to delete the Game.start_of_1st_half ... Game.finished_time
 */
export default class PhaseChangeEventDialog extends React.Component {
    constructor (props) {
        super(props);

        //Mapping of all phases a game can be in, including
        // a) alternative phases that we can transition into from the phase
        // b) tag to export when transitioning into the phase
        this.possiblePhases = {
            "not started": {
                alternatives: ["1st half"],
                tag: null,//Initial phase, can never transition into this phase
            },
            "1st half": {
                alternatives: ["halftime break"],
                tag: {
                    action: "start phase",
                    phase: {type: "phase", value: "1st half"},
                    team: {type: "team", id: null, value: null}
                }
            },
            "halftime break": {
                alternatives: ["2nd half"],
                tag: {
                    action: "end phase",
                    phase: {type: "phase", value: "1st half"}
                }
            },
            "2nd half": {
                alternatives: ["finished", "fulltime break"],
                tag: {
                    action: "start phase",
                    phase: {type: "phase", value: "2nd half"},
                    team: {type: "team", id: null, value: null}
                }
            },
            "fulltime break": {
                alternatives: ["1st overtime"],
                tag: {
                    action: "end phase",
                    phase: {type: "phase", value: "2nd half"}
                }
            },
            "1st overtime": {
                alternatives: ["overtime break"],
                tag: {
                    action: "start phase",
                    phase: {type: "phase", value: "1st overtime"},
                    team: {type: "team", id: null, value: null}
                }
            },
            "overtime break": {
                alternatives: ["2nd overtime"],
                tag: {
                    action: "end phase",
                    phase: {type: "phase", value: "1st overtime"}
                }
            },
            "2nd overtime": {
                alternatives: ["finished", "penalties break"],
                tag: {
                    action: "start phase",
                    phase: {type: "phase", value: "2nd overtime"},
                    team: {type: "team", id: null, value: null}
                }
            },
            "penalties break": {
                alternatives: ["penalties"],
                tag: {
                    action: "end phase",
                    phase: {type: "phase", value: "2nd overtime"}
                }
            },
            "penalties": {
                alternatives: ["finished"],
                tag: {
                    action: "start phase",
                    phase: {type: "phase", value: "penalties"},
                    team: {type: "team", id: null, value: null}
                }
            },
            "finished": {
                alternatives: [],//Last phase, nothing to transition to
                tag: {
                    action: "end of game"
                }
            },
        };

        //NOTE: Does not respond to changes to props, assumed to be recreated each time
        let currentPhase = this.possiblePhases[this.props.game.phase];
        if (currentPhase === undefined) {
            console.error("Unknown game phase", this.props.game.phase);
        }

        const nextPhase = currentPhase.alternatives[0];
        const lastTeamId = this.getLastTeamIdUsed();
        console.log("Last team id used for kickoff =", lastTeamId);
        const team = lastTeamId === this.props.homeTeam.id ? this.props.visitingTeam : this.props.homeTeam;

        const noStoppages = this.props.game.stop_periods === null || this.props.game.stop_periods.length % 2 === 0;
        this.state = {
            nextPhase: nextPhase,
            team: team,
            lastKickoffTeamId: lastTeamId,
            stoppageVisible: !noStoppages,
        };
    }

    getLastTeamIdUsed () {
        const events = this.props.existingEvents;
        if (!events) return undefined;

        // Note: Due to extra time, there can be more than 1
        const firstHalves = events.filter((e) => e.tag && e.tag.action === "start phase");
        if (firstHalves.length === 0) return undefined;

        const last = firstHalves[firstHalves.length - 1];
        if (last.tag.team) return last.tag.team.id;
        return undefined;
    }

    isStartPhase (phase) {
        return phase && "team" in phase.tag;
    }

    @bind
    save () {
        if (this.state.nextPhase === undefined) {
            return;
        }
        this.props.onSave(this.getTag());
    }

    @bind
    saveNow () {
        if (this.state.nextPhase === undefined) {
            return;
        }
        this.props.onSave(this.getTag(), undefined, undefined, true);
    }

    getTag () {
        let phase = this.possiblePhases[this.state.nextPhase];
        let tag = phase.tag;
        if (this.isStartPhase(phase)) {
            tag = JSON.parse(JSON.stringify(tag)); // deep copy before mutating
            tag.team.id = this.state.team.id;
            tag.team.value = this.state.team.name;
        }
        return tag;
    }

    @bind
    setNextPhase (phase) {
        this.setState({nextPhase: phase});
    }

    @bind
    setTeam (teamName) {
        let team = null;
        if (teamName === this.props.homeTeam.name) {
            team = this.props.homeTeam;
        } else if (teamName === this.props.visitingTeam.name) {
            team = this.props.visitingTeam;
        }
        this.setState({team: team});
    }

    render () {

        if (this.state.stoppageVisible) {
            return (
                <StopClockOverlay
                    hotkeyHandler={this.props.hotkeyHandler}
                    getTimestamp={this.props.getTimestamp}
                    server={this.props.server}
                    onCancel={()=>this.setState({stoppageVisible: false})}
                    onDone={()=>this.setState({stoppageVisible: false}, this.props.onCancel)}
                    game={this.props.game}
                />
            );
        }

        let currentPhase = this.possiblePhases[this.props.game.phase];
        let nextPhase = currentPhase.alternatives.length > 0 && this.state.nextPhase ?
            this.possiblePhases[this.state.nextPhase] : null;

        return (
            <DialogOverlay
                hotkeyHandler={this.props.hotkeyHandler}
                onSubmit={this.save}
                onSecondarySubmit={this.saveNow}
                secondarySubmitText="Save (now)"
                onCancel={this.props.onCancel}>
                <a className="special-stoppage-btn btn btn-xs btn-default btn-transparent text-outline"
                   onClick={()=>this.setState({stoppageVisible: !this.state.stoppageVisible})}>
                    Halt match
                </a>
                <div className="row" style={{marginBottom: "16px"}}>
                    <div className="col-xs-12 text-center text-capitalize">
                        Change game phase
                    </div>
                </div>
                {currentPhase.alternatives.length === 0 &&
                    <div className="col-xs-12 text-center">
                        <span style={{color: "red", fontWeight: "bold", fontSize: "larger"}}>
                            Game is over
                        </span>
                    </div>
                }
                {currentPhase.alternatives.length > 0 &&
                    <ButtonSelectionGroup
                        onSelect={this.setNextPhase}
                        selected={this.state.nextPhase}
                        options={currentPhase.alternatives} />
                }
                {(nextPhase !== null && this.isStartPhase(nextPhase)) &&
                    <ButtonSelectionGroup
                        style={{marginTop: "8px"}}
                        isReadOnly={!!this.state.lastKickoffTeamId}
                        onSelect={this.setTeam}
                        selected={this.state.team.name}
                        options={[this.props.homeTeam.name, this.props.visitingTeam.name]} />
                }
            </DialogOverlay>
        );
    }
}



// WEBPACK FOOTER //
// ./src/js/PhaseChangeEventDialog.js